(function () {
  // Prevent double initialization
  if (typeof window !== 'undefined' && window.StickyManager) {
    console.warn('StickyManager already initialized');
    return;
  }

  const StickyManager = (function () {
    const items = new Map(); // id -> {el, container, geom, state, opts}
    let nextId = 1;
    let ticking = false;
    let lastScrollY = 0;
    let globalTopOffset = 0; // Global offset applied to all items unless overridden

    //
    // ResizeObserver to monitor element/container size changes
    //

    const ro = new ResizeObserver((entries) => {
      // Any container/element size change -> mark its item dirty
      for (const { target } of entries) {
        for (const [id, item] of items) {
          if (item.container === target || item.el === target) {
            item.dirty = true;
          }
        }
      }

      schedule();
    });

    //
    // Event hooks
    //

    const onScroll = () => {
      // Skip if there are no items
      if (items.size === 0) return;

      lastScrollY = window.scrollY || window.pageYOffset;
      schedule();
    };

    const onResize = () => {
      // Skip if there are no items
      if (items.size === 0) return;

      markAllDirty();
      schedule();
    };

    //
    // Update cycle
    //

    function schedule() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(tick);
      }
    }

    function tick() {
      // 1) Reads (geom) for dirty items
      for (const item of items.values()) {
        if (item.dirty || !item.geom) recomputeGeometry(item);
      }

      // 2) For all items: compute state + edge offsets, then write to data/vars
      for (const item of items.values()) {
        const newState = computeState(item);
        if (newState !== item.state) {
          item.state = newState;
          item.el.dataset.sticky = newState; // "top" | "fixed" | "bottom"
        }
      }

      ticking = false;
    }

    //
    // Helpers
    //

    function markAllDirty() {
      for (const item of items.values()) item.dirty = true;
    }

    function getEffectiveTopOffset(item) {
      // Per-item topOffset takes precedence over global
      return item.opts.topOffset ?? globalTopOffset;
    }

    function recomputeGeometry(item) {
      // Cache page-relative positions to avoid extra layouts
      const containerRect = item.container.getBoundingClientRect();
      const pageTop = (window.scrollY || window.pageYOffset) + containerRect.top;
      const containerHeight = item.container.clientHeight; // content box height
      const elHeight = item.el.offsetHeight; // full element height for bottom clamp
      const topOffset = getEffectiveTopOffset(item);
      const maxScrollInside = Math.max(0, containerHeight - elHeight);

      item.geom = { pageTop, containerHeight, elHeight, maxScrollInside, topOffset };
      item.dirty = false;

      // Update edge CSS variables
      writeEdgeVars(item);
      writeTopVar(item);
    }

    function computeState(item) {
      const { pageTop, maxScrollInside, topOffset } = item.geom;

      // Account for topOffset: element becomes fixed when container top reaches topOffset from viewport top
      // This happens when: lastScrollY = pageTop - topOffset
      // offsetInside represents how far into the container we've scrolled (accounting for topOffset)
      const offsetInside = Math.min(Math.max(lastScrollY - pageTop + topOffset, 0), maxScrollInside);

      if (offsetInside <= 0) return 'top';
      if (offsetInside >= maxScrollInside) return 'bottom';
      return 'fixed';
    }

    //
    // Edge CSS variables and calculations
    //

    function resolveEdgeReferenceWidth(item) {
      // 1) explicit number wins (treated as px)
      if (typeof item.opts.edgeWidth === 'number') return item.opts.edgeWidth;

      // 2) CSS variable (supports px or %). Default scope is :root when edgeVarElement is not provided.
      if (item.opts.edgeCssVar) {
        const node = item.opts.edgeVarElement || document.documentElement;
        const varName = item.opts.edgeCssVar.trim();
        const raw = getComputedStyle(node).getPropertyValue(varName).trim();

        if (raw.endsWith('%')) {
          const refWidth = parseFloat(raw.replace('px', ''));
          if (Number.isFinite(refWidth)) {
            const vw = document.documentElement.clientWidth;
            return Math.max(0, (refWidth / 100) * vw);
          }
        } else if (raw.endsWith('px') || raw === '0') {
          const refWidth = parseFloat(raw.replace('px', ''));
          if (Number.isFinite(refWidth)) return Math.max(0, refWidth);
        }
        // any other unit -> fall through
      }

      // 3) edgeContainer's used width (content box). If not provided, use item.container as fallback.
      const edgeContainer =
        (typeof item.opts.edgeContainer === 'string'
          ? document.querySelector(item.opts.edgeContainer)
          : item.opts.edgeContainer) ?? item.container;

      const rect = edgeContainer.getBoundingClientRect();
      return rect.width;
    }

    function writeEdgeVars(item) {
      const vw = document.documentElement.clientWidth;
      const refWidth = Math.min(resolveEdgeReferenceWidth(item), vw);

      if (item.opts.edgeWidth != null || item.opts.edgeCssVar || item.opts.edgeContainer) {
        // Assume the reference block is horizontally centered within the viewport
        const margin = Math.max(0, (vw - refWidth) / 2);
        item.el.style.setProperty('--sticky-pos-left', `${margin}px`);
        item.el.style.setProperty('--sticky-pos-right', `${margin}px`);
        return;
      }

      // Fallback: use live container edges relative to viewport
      const rect = item.container.getBoundingClientRect();
      const left = Math.max(0, rect.left);
      const right = Math.max(0, vw - rect.right);

      // Set as px values. Consumer CSS can choose left/right anchoring.
      item.el.style.setProperty('--sticky-pos-left', `${left}px`);
      item.el.style.setProperty('--sticky-pos-right', `${right}px`);
    }

    function writeTopVar(item) {
      const topOffset = getEffectiveTopOffset(item);
      item.el.style.setProperty('--sticky-pos-top', `${topOffset}px`);
    }

    //
    // Public methods
    //

    function add({
      el,
      container,
      topOffset = null,
      edgeWidth = null,
      edgeCssVar = null,
      edgeVarElement = null,
      edgeContainer = null,
    }) {
      if (typeof el === 'string') el = document.querySelector(el);
      if (typeof container === 'string') container = document.querySelector(container);
      if (typeof container === 'undefined' && el)
        container = document.querySelector(el.dataset.stickyContainer) || document.body;

      if (!el || !container) throw new Error('Provide { el, container }');

      // First check if element already added bases in data-stick-id attribute
      if (el.dataset.stickyId) {
        const existingId = parseInt(el.dataset.stickyId);
        if (items.has(existingId)) return existingId; // already added
      }

      const id = nextId++;

      // Set a data attribute so we can detect duplicates
      el.dataset.stickyId = id;

      const item = {
        el,
        container,
        geom: null,
        state: '',
        dirty: true,
        opts: { topOffset, edgeWidth, edgeCssVar, edgeVarElement, edgeContainer },
      };

      items.set(id, item);

      ro.observe(el);
      ro.observe(container);

      // First calc + paint
      lastScrollY = window.scrollY || window.pageYOffset;
      schedule();

      return id;
    }

    function remove(id) {
      if (id instanceof Element) id = parseInt(id.dataset.stickyId, 10) || 0;
      if (!id) return;

      const item = items.get(id);
      if (!item) return;

      // Stop observing
      ro.unobserve(item.el);
      ro.unobserve(item.container);

      // Cleanup
      item.el.removeAttribute('data-sticky-id');
      item.el.removeAttribute('data-sticky');
      item.el.style.removeProperty('--sticky-pos-left');
      item.el.style.removeProperty('--sticky-pos-right');
      item.el.style.removeProperty('--sticky-pos-top');

      items.delete(id);
    }

    function removeAll() {
      for (const id of items.keys()) remove(id);
      ticking = false;
      lastScrollY = 0;
      nextId = 1;
    }

    function refresh(id) {
      if (id) {
        const it = items.get(id);
        if (it) it.dirty = true;
      } else {
        markAllDirty();
      }
      schedule();
    }

    function setTopOffset(id, offset) {
      if (typeof offset !== 'number' || offset < 0) {
        throw new Error('topOffset must be a non-negative number');
      }

      const item = items.get(id);
      if (!item) {
        throw new Error(`Item with id ${id} not found`);
      }

      item.opts.topOffset = offset;
      item.dirty = true;
      schedule();
    }

    function setGlobalTopOffset(offset) {
      if (typeof offset !== 'number' || offset < 0) {
        throw new Error('topOffset must be a non-negative number');
      }

      globalTopOffset = offset;
      markAllDirty();
      schedule();
    }

    function getTopOffset(id) {
      if (id) {
        const item = items.get(id);
        if (!item) return null;
        return getEffectiveTopOffset(item);
      }
      return globalTopOffset;
    }

    //
    // Add event listeners
    //

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener(
      'load',
      () => {
        markAllDirty();
        schedule();
      },
      { passive: true }
    );

    return {
      add,
      remove,
      removeAll,
      refresh,
      setTopOffset,
      setGlobalTopOffset,
      getTopOffset,
    };
  })();

  // UMD export pattern - works in Node.js and browsers
  if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = StickyManager;
  } else if (typeof window !== 'undefined') {
    window.StickyManager = StickyManager;
  }
})();
